import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { auth } from '@/js/firebase'
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, onAuthStateChanged, OAuthProvider, signInWithPopup, GoogleAuthProvider } from 'firebase/auth'
import { useFirebaseAppStore } from '@/stores/storeFirebaseApp'
import { SIGNED_OUT_ROUTE, requiresRedirectOnSignOut } from '@/services/authRedirect'

export const useAuthStore = defineStore('auth', () => {
    const user = ref({
        fullname: '',
        email: '',
        uid: '',
        isExternalProvider: false
    })

    const errorsOccurred = ref(false)
    const errorMessage = ref({
        message: '',
        code: 0,
    })
    const debounce = ref(true)

    const delay = ms => new Promise(res => setTimeout(res, ms));

    async function init() {
        const firebaseAppStore = useFirebaseAppStore()
        // Global authentication handler
        onAuthStateChanged(auth, (user) => {
            if (user) {
                this.user.email = user.email
                this.user.uid = user.uid
                console.log('User is signed in user=', user)
                if (!this.user.isExternalProvider) {
                    // firebaseAppStore.init() already catches everything
                    // internally and always resolves (see its own try/catch),
                    // so this .catch() is just a last-line-of-defence safety
                    // net -- without it, any future uncaught throw here would
                    // again become a silent unhandled rejection that leaves
                    // the user stuck on /login despite Auth having succeeded.
                    firebaseAppStore.init(this.user).catch((error) => {
                        console.error('firebaseAppStore.init failed:', error)
                        this.errorMessage.message = 'Something went wrong finishing sign-in. Please try again.'
                        this.errorMessage.code = 0
                        this.errorsOccurred = true
                    })
                    // Redirection is now handled by firebaseAppStore.init() based on shared reports
                }
            } else {
                console.log('User is signed out')
                this.user.fullname = ''
                this.user.email = ''
                this.user.uid = ''
                firebaseAppStore.clearCaches()
                // Leave a public page alone, but never leave a protected one
                // rendered with nobody signed in -- see
                // requiresRedirectOnSignOut for why this reads route meta
                // rather than matching on paths.
                if (requiresRedirectOnSignOut(this.router.currentRoute.value)) {
                    this.router.replace(SIGNED_OUT_ROUTE)
                }
            }
        })
        await delay(1000)
        this.debounce = false
    }

    function loginWithUsernameAndPassword(email, password) {
        this.user.isExternalProvider = false
        signInWithEmailAndPassword(auth, email, password).then((userCredential) => {
            // Signed in
            //user = userCredential.user
            // ...
        }
        ).catch((error) => {
            const errorCode = error.code;
            const errorMsg = error.message;

            this.errorMessage.message = 'Error: ' + errorMsg
            this.errorMessage.code = errorCode
            this.errorsOccurred = true
            console.log("Error occurred in Login: ", errorMsg)
        })
    }

    function loginWithApple() {
        const provider = new OAuthProvider('apple.com')
        provider.addScope('email')
        provider.addScope('name')
        // fladd this is Apple signin
        this.user.isExternalProvider = true
        signInWithPopup(auth, provider)
            .then((result) => {
                // The signed-in user info.
                const user = result.user

                // Apple credential
                const credential = OAuthProvider.credentialFromResult(result)
                const accessToken = credential.accessToken
                const idToken = credential.idToken
                console.log('Apple signIn Credential=', credential, ' access toke=', accessToken, ' idToken=', idToken)
                this.user.fullname = user.displayName ?? user.email
                this.user.email = user.email
                this.user.uid = user.uid
                const firebaseAppStore = useFirebaseAppStore()
                firebaseAppStore.init(this.user)
                if (this.router.currentRoute.value.path !== '/oauth/consent') {
                    this.router.push('/')
                }
                // IdP data available using getAdditionalUserInfo(result)
                // ...
            })
            .catch((error) => {
                // Handle Errors here.
                const errorCode = error.code
                const errorMessage = error.message
                // The email of the user's account used.
                const email = error.customData.email
                // The credential that was used.
                const credential = OAuthProvider.credentialFromError(error)
                console.log('Apple sign error err=', errorMessage, ' email=', email, 'credential err=', credential)

            })
    }

    function loginWithGoogle() {
        const provider = new GoogleAuthProvider()
        provider.addScope('https://www.googleapis.com/auth/userinfo.email')
        provider.addScope('https://www.googleapis.com/auth/userinfo.profile')
        // fladd this is Apple signin
        this.user.isExternalProvider = true
        signInWithPopup(auth, provider)
            .then((result) => {
                // The signed-in user info.
                const user = result.user

                // Apple credential
                const credential = GoogleAuthProvider.credentialFromResult(result)
                const accessToken = credential.accessToken
                const idToken = credential.idToken
                console.log('Google signIn Credential=', credential, ' access toke=n', accessToken, ' idToken=', idToken)
                this.user.fullname = user.displayName ?? user.email
                this.user.email = user.email
                this.user.uid = user.uid
                const firebaseAppStore = useFirebaseAppStore()
                firebaseAppStore.init(this.user)
                if (this.router.currentRoute.value.path !== '/oauth/consent') {
                    this.router.push('/')
                }
                
            })
            .catch((error) => {
                // Handle Errors here.
                const errorCode = error.code
                const errorMessage = error.message
                // The email of the user's account used.
                const email = error.customData.email
                // The credential that was used.
                const credential = OAuthProvider.credentialFromError(error)
                console.log('Apple sign error err=', errorMessage, ' email=', email, 'credential err=', credential)

            })
    }

    async function logout() {
        await auth.signOut()
    }

    async function register(fullname, email, password) {
        // capture the fullname in the state to signify that this is a new user
        this.user.fullname = fullname
        try {
            await createUserWithEmailAndPassword(auth, email, password)
            // user should be logged in and handled in the global authentication handler
            console.log('User registered')
        } catch (error) {
            this.errorMessage.message = 'Error: ' + error.message
            this.errorMessage.code = error.code
            this.errorsOccurred = true
            console.log(error.message)
            throw error
        }
    }
    return { user, loginWithUsernameAndPassword, logout, register, init, debounce, loginWithApple, loginWithGoogle, errorsOccurred, errorMessage }
})
